import React, { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { Card, Title, Text, Button, Group, Stack, Alert, ThemeIcon } from '@mantine/core';
import { IconAlertTriangle, IconRefresh, IconHome, IconHeart } from '@tabler/icons-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

// Error boundary class component for Parent Portal
class ParentErrorBoundaryClass extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Parent Portal Error:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo,
    });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return <>{this.props.fallback}</>;
      }

      return <ErrorFallback error={this.state.error} onReset={this.handleReset} />;
    }

    return this.props.children;
  }
}

// Functional component for error display
const ErrorFallback: React.FC<{
  error: Error | null;
  onReset: () => void;
}> = ({ error, onReset }) => {
  const handleGoHome = () => {
    window.location.href = '/parent/dashboard';
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      padding: '20px',
      backgroundColor: 'var(--mantine-color-pink-0)'
    }}>
      <Card 
        shadow="md" 
        padding="xl" 
        radius="md" 
        withBorder
        style={{ maxWidth: 500, width: '100%' }}
      >
        <Stack gap="lg" align="center" ta="center">
          <ThemeIcon size={64} radius="xl" color="pink" variant="light">
            <IconHeart size={32} />
          </ThemeIcon>
          
          <div>
            <Title order={2} mb="xs">Oops! Something went wrong</Title>
            <Text size="sm" c="dimmed">
              We're sorry, but we couldn't load this page properly.
            </Text>
          </div>

          <Alert 
            icon={<IconAlertTriangle size={16} />} 
            color="pink" 
            variant="light"
            w="100%"
          >
            <Text size="sm">
              {error?.message || 'An unexpected error occurred while loading this page.'}
            </Text>
          </Alert>

          <Text size="xs" c="dimmed">
            Don't worry, your child's information is safe. This is just a temporary issue.
          </Text>

          <Group justify="center" mt="md">
            <Button
              variant="filled"
              color="pink"
              leftSection={<IconRefresh size={16} />}
              onClick={onReset}
            >
              Try Again
            </Button>
            <Button
              variant="light"
              color="pink"
              leftSection={<IconHome size={16} />}
              onClick={handleGoHome}
            >
              Go to Dashboard
            </Button>
          </Group>

          <Text size="xs" c="dimmed" mt="md">
            If this problem continues, please contact your school administrator.
          </Text>
        </Stack>
      </Card>
    </div>
  );
};

// Export the wrapper component
export const ParentErrorBoundary: React.FC<Props> = (props) => {
  return <ParentErrorBoundaryClass {...props} />;
};

export default ParentErrorBoundary;